import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HelpCircle, Plus, Trash2, ArrowUp, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none text-sm';

const FaqItemEditor = ({ item, i, onChange, onRemove, onUp }) => (
  <div className="p-4 rounded-xl border border-[#E6DAC5] space-y-2" data-testid={`faq-editor-item-${i}`}>
    <div className="flex gap-2">
      <input value={item.q} onChange={(e) => onChange({ ...item, q: e.target.value })} placeholder="Question" data-testid={`faq-editor-q-${i}`} className={inputCls} />
      {i > 0 && <button type="button" onClick={onUp} title="Move up" data-testid={`faq-editor-up-${i}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg"><ArrowUp size={16} /></button>}
      <button type="button" onClick={onRemove} title="Remove" data-testid={`faq-editor-remove-${i}`} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
    </div>
    <textarea rows={3} value={item.a} onChange={(e) => onChange({ ...item, a: e.target.value })} placeholder="Answer" data-testid={`faq-editor-a-${i}`} className={inputCls} />
  </div>
);

export const FaqEditorPanel = ({ authHeaders }) => {
  const [dests, setDests] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/faqs`, authHeaders).then((r) => {
      setDests(r.data.destinations);
      const first = r.data.destinations[0];
      if (first) { setActive(first.slug); setItems(first.items); }
    }).catch(() => toast.error('Could not load FAQs'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (d) => { setActive(d.slug); setItems(d.items); };
  const update = (i, v) => setItems(items.map((x, j) => (j === i ? v : x)));
  const moveUp = (i) => { const n = [...items]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setItems(n); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/faqs/${active}`, { items }, authHeaders);
      setItems(r.data.items);
      setDests(dests.map((d) => (d.slug === active ? { ...d, items: r.data.items } : d)));
      toast.success('FAQs saved');
    } catch { toast.error('Could not save FAQs'); }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="faq-editor-panel">
      <h3 className="font-semibold text-[#1E1622] flex items-center gap-2"><HelpCircle size={18} className="text-[#C5A059]" /> Guide FAQs</h3>
      <p className="text-sm text-[#1E1622]/60 mb-4">Shown on each country's destination page and to Google as expandable questions. Empty questions are removed when you save.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {dests.map((d) => (
          <button key={d.slug} type="button" onClick={() => pick(d)} data-testid={`faq-dest-${d.slug}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${active === d.slug ? 'bg-[#3B164C] text-white border-[#3B164C]' : 'border-[#E6DAC5] text-[#1E1622]/70 hover:border-[#C5A059]'}`}>
            {d.destination} <span className="opacity-60">({d.items.length})</span>
          </button>
        ))}
      </div>
      {active && (
        <div className="space-y-3">
          {items.map((it, i) => <FaqItemEditor key={i} item={it} i={i} onChange={(v) => update(i, v)} onRemove={() => setItems(items.filter((_, j) => j !== i))} onUp={() => moveUp(i)} />)}
          <div className="flex flex-wrap gap-3 pt-2">
            <button type="button" onClick={() => setItems([...items, { q: '', a: '' }])} data-testid="faq-editor-add" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#3B164C]/30 text-[#3B164C] text-sm hover:border-[#C5A059]"><Plus size={16} /> Add question</button>
            <button type="button" onClick={save} disabled={saving} data-testid="faq-editor-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#3B164C] text-white text-sm disabled:opacity-50"><Save size={16} /> {saving ? 'Saving…' : 'Save FAQs'}</button>
          </div>
        </div>
      )}
    </div>
  );
};
